// Реестр ПЛАНОВЫХ ЛОКАЛЕЙ СБОРА — единственный источник ответа на вопрос
// «какие локали мы собираем и как код связан с языком и страной каталога».
//
// ЛОКАЛЬ = ЯЗЫК + СТРАНА (правило Глеба, 2026-08-24). Кодов ровно 35, и это
// число — не совпадение: столько ячеек считает клиентский трекер
// (./pothos/tracker-locales.ts, лист «By locale»).
//
// НОРВЕЖСКИЙ (решение Глеба 2026-08-01, подтверждено 2026-08-25): собирается
// в Британии, Дании, Швеции и Канаде — четыре кода no_GB/no_DK/no_SE/no_CA.
// Самой Норвегии среди стран сбора НЕТ, поэтому кода no_NO в плане нет:
// это билеты норвежскоязычных покупателей на события в ДРУГИХ странах.
// Квота у четырёх ячеек ЕДИНАЯ на семью (вкладки трекера по 1 796 — разрез
// для отчёта, в сумме 7 184 = объём одной обычной локали).
//
// Историческая справка, почему реестр появился (аудит 2026-08-25):
// до него «список локалей» жил в 23+ независимых местах (LOCALE_LIMITS,
// PACK_LOCALES, три копии COUNTRY_ISO, два конвертера-близнеца в
// apps/api и apps/work-api…), и они разошлись — no_CA был заведён в шести
// местах из одиннадцати необходимых, а план задвоился на 7 192 ассета.
import { z } from 'zod';

export interface PlanLocale {
  /** Канонический код xx_YY — подчёркивание, язык в нижнем, страна в верхнем. */
  code: string;
  /** ISO-639-1 языка ('es'; у китайского 'zh' — письмо решает страна). */
  lang: string;
  /** ISO-3166 страны сбора ('US'). */
  iso: string;
  /** Имя языка в вокабуляре каталога pothos_assets.locale ('Spanish'). */
  language: string;
  /** Имя страны в вокабуляре каталога pothos_assets.country ('United_States'). */
  country: string;
  /**
   * Легаси-код ХРАНЕНИЯ ('ES', 'ZH-HANS') — то, что лежит в старых колонках
   * registrations.locale / evaluations.locale / des_*.locale. Новый код его
   * больше не пишет; карта нужна читателям исторических строк.
   */
  legacy: string;
}

const P = (
  code: string, lang: string, iso: string,
  language: string, country: string, legacy: string,
): PlanLocale => ({ code, lang, iso, language, country, legacy });

/** 35 плановых локалей сбора. Порядок — алфавит кода. */
export const PLAN_LOCALES: readonly PlanLocale[] = [
  P('ar_AE', 'ar', 'AE', 'Arabic', 'United_Arab_Emirates', 'AR'),
  P('ar_SA', 'ar', 'SA', 'Arabic', 'Saudi_Arabia', 'AR'),
  P('da_DK', 'da', 'DK', 'Danish', 'Denmark', 'DK'),
  P('de_CH', 'de', 'CH', 'German', 'Switzerland', 'DE'),
  P('de_DE', 'de', 'DE', 'German', 'Germany', 'DE'),
  P('es_CL', 'es', 'CL', 'Spanish', 'Chile', 'ES'),
  P('es_ES', 'es', 'ES', 'Spanish', 'Spain', 'ES'),
  P('es_MX', 'es', 'MX', 'Spanish', 'Mexico', 'ES'),
  P('es_US', 'es', 'US', 'Spanish', 'United_States', 'ES'),
  P('fi_FI', 'fi', 'FI', 'Finnish', 'Finland', 'FI'),
  P('fr_CA', 'fr', 'CA', 'French', 'Canada', 'FR'),
  P('fr_FR', 'fr', 'FR', 'French', 'France', 'FR'),
  P('he_IL', 'he', 'IL', 'Hebrew', 'Israel', 'HE'),
  P('hi_IN', 'hi', 'IN', 'Hindi', 'India', 'HI'),
  P('id_ID', 'id', 'ID', 'Indonesian', 'Indonesia', 'ID'),
  P('it_CH', 'it', 'CH', 'Italian', 'Switzerland', 'IT'),
  P('it_IT', 'it', 'IT', 'Italian', 'Italy', 'IT'),
  P('ja_JP', 'ja', 'JP', 'Japanese', 'Japan', 'JP'),
  P('ko_KR', 'ko', 'KR', 'Korean', 'South_Korea', 'KR'),
  P('ms_MY', 'ms', 'MY', 'Malaysian', 'Malaysia', 'MY'),
  P('nl_NL', 'nl', 'NL', 'Dutch', 'Netherlands', 'NL'),
  P('no_CA', 'no', 'CA', 'Norwegian', 'Canada', 'NO'),
  P('no_DK', 'no', 'DK', 'Norwegian', 'Denmark', 'NO'),
  P('no_GB', 'no', 'GB', 'Norwegian', 'United_Kingdom', 'NO'),
  P('no_SE', 'no', 'SE', 'Norwegian', 'Sweden', 'NO'),
  P('pl_PL', 'pl', 'PL', 'Polish', 'Poland', 'PL'),
  P('pt_BR', 'pt', 'BR', 'Portuguese', 'Brazil', 'PT'),
  P('pt_PT', 'pt', 'PT', 'Portuguese', 'Portugal', 'PT'),
  P('sv_SE', 'sv', 'SE', 'Swedish', 'Sweden', 'SE'),
  P('th_TH', 'th', 'TH', 'Thai', 'Thailand', 'TH'),
  P('tr_TR', 'tr', 'TR', 'Turkish', 'Turkey', 'TR'),
  P('vi_VN', 'vi', 'VN', 'Vietnamese', 'Vietnam', 'VN'),
  P('zh_HK', 'zh', 'HK', 'Traditional_Chinese', 'Hong_Kong', 'ZH-HANT'),
  P('zh_SG', 'zh', 'SG', 'Simplified_Chinese', 'Singapore', 'ZH-HANS'),
  P('zh_TW', 'zh', 'TW', 'Traditional_Chinese', 'Taiwan', 'ZH-HANT'),
];

/** Все 35 кодов. */
export const PLAN_CODES: readonly string[] = PLAN_LOCALES.map(l => l.code);

/**
 * ВОКАБУЛЯР ВХОДА для поверхностей, заводящих НОВЫЕ ячейки pothos/work
 * (dev-карточки, research-кампании и т.п.): только разрешённые коды проекта.
 * Возникший в процессе en_CA/en_SA «не подходит, потому что он не в списке
 * разрешённых кодов» (Глеб, 2026-08-25). Историчeские строки это не трогает —
 * вокабуляр хранения шире вокабуляра входа.
 */
export const PlanCodeSchema = z.enum(PLAN_CODES as [string, ...string[]]);

// Коды, которые встречаются в ИСТОРИЧЕСКИХ строках, но в плане отсутствуют.
// Читатель старых данных обязан их узнавать, писатель — не порождать.
//   no_NO — «весь норвежский» до расщепления на четыре страны; страна из кода
//           не выводится, реальную ищи в merchant_region / country строки;
//   zh_CN — переименован в zh_SG (сиды 0040a→0051), остался в бэкфилле 0043b;
//   zh_US — тень легаси-строки ZH-HANS с регионом US; локалью сбора не был.
export const RETIRED_PLAN_CODES: readonly string[] = ['no_NO', 'zh_CN', 'zh_US'];

const BY_CODE = new Map<string, PlanLocale>(PLAN_LOCALES.map(l => [l.code, l]));

/** Нормализация написания кода: 'FR-fr' → 'fr_FR'. Форму не проверяет. */
export function normPlanCode(raw: string): string {
  const s = String(raw || '').trim().replace(/-/g, '_');
  const m = /^([a-z]{2,3})_([a-z]{2})$/i.exec(s);
  return m ? m[1].toLowerCase() + '_' + m[2].toUpperCase() : s;
}

/** Плановая локаль по коду в любом написании. null — кода в плане нет. */
export function planLocale(raw: string): PlanLocale | null {
  return BY_CODE.get(normPlanCode(raw)) || null;
}

/**
 * Ячейка каталога по коду: {loc: имя языка, ctry: имя страны}.
 * null = код вне плана; НЕ читай по нему реестры — пустой ответ неотличим
 * от «ничего не собрано» (правило .claude/rules/locale-cap-and-dedup.md).
 */
export function planCellFromCode(raw: string): { loc: string; ctry: string } | null {
  const l = planLocale(raw);
  return l ? { loc: l.language, ctry: l.country } : null;
}

/**
 * Пара каталога (имя языка, имя страны) → код плана. '' — пары в плане нет:
 * её должен увидеть человек, а не тихо получить чужую ячейку. В частности,
 * (Norwegian, Norway) даёт '' НАМЕРЕННО — страна Norway в каталоге всегда
 * ошибка разметки.
 */
export function planCodeForCell(language: string, country: string): string {
  const lang = String(language || '').trim();
  const ctry = String(country || '').trim();
  for (const l of PLAN_LOCALES) if (l.language === lang && l.country === ctry) return l.code;
  return '';
}

/**
 * Легаси-код хранения для планового кода. '' — код вне плана. Никогда не
 * выводит легаси из строки кода: прежний fallback parts[1].toUpperCase()
 * подставлял КОД СТРАНЫ вместо языка ('no_CA' → 'CA') и ломал round-trip
 * приёма (аудит 2026-08-25).
 */
export function legacyForPlanCode(raw: string): string {
  return planLocale(raw)?.legacy || '';
}

/**
 * Семья плановых кодов легаси-кода хранения: 'NO' → четыре норвежских,
 * 'ES' → четыре испанских. Пустой массив — легаси не узнан.
 */
export function planCodesForLegacy(legacy: string): string[] {
  const up = String(legacy || '').trim().toUpperCase();
  return PLAN_LOCALES.filter(l => l.legacy === up).map(l => l.code);
}

/**
 * Единственный плановый код для пары (легаси-код, ISO-2 страны): 'NO'+'GB' →
 * 'no_GB'. '' — в плане нет такой ячейки; НЕ доминируй в «главную страну
 * семьи» — молчаливый доминант и породил задвоенный норвежский план.
 */
export function planCodeForLegacy(legacy: string, countryIso: string): string {
  const iso = String(countryIso || '').trim().toUpperCase();
  const fam = planCodesForLegacy(legacy);
  for (const code of fam) { if (BY_CODE.get(code)!.iso === iso) return code; }
  return '';
}

// ─── Квотные ключи: 35 ячеек, но 32 квоты ────────────────────────────────────
// ЛИМИТЫ норвежского считаются ПО ЯЗЫКУ, а не по стране (Глеб, 2026-08-01,
// подтверждено 2026-08-25): «900 фотографий гифт-карт из Швеции на норвежском
// полностью закрывают весь кап по этой категории среди всех четырёх». Четыре
// кода no_* — варианты, ГДЕ можно собирать; квота у них одна — ключ 'no'.
// Капы МЕРЧАНТОВ при этом остаются внутри страны на языке: McDonald's
// по-норвежски в Канаде и в Дании друг к другу не относятся.
export const NORWEGIAN_QUOTA_KEY = 'no';

/** Квотный ключ кода: 'no' для норвежской семьи, иначе сам код. */
export function planQuotaKey(raw: string): string {
  const l = planLocale(raw);
  if (l) return l.lang === 'no' ? NORWEGIAN_QUOTA_KEY : l.code;
  const s = normPlanCode(raw);
  // Вышедший no_NO живёт в исторических строках — его квота тоже семейная.
  return s === 'no_NO' ? NORWEGIAN_QUOTA_KEY : s;
}

/**
 * Все коды, чьё ПОТРЕБЛЕНИЕ входит в квоту данного кода: норвежский —
 * вся семья плюс исторический no_NO (старые строки продолжают занимать
 * квоту), остальные — сами по себе. Для SQL `locale_code IN (…)`.
 */
export function quotaCodesFor(raw: string): string[] {
  return planQuotaKey(raw) === NORWEGIAN_QUOTA_KEY
    ? ['no_CA', 'no_DK', 'no_GB', 'no_SE', 'no_NO']
    : [normPlanCode(raw)];
}

// ─── Канонизация неканоничных ячеек ─────────────────────────────────────────
// Коды вне плана (en_SA, en_CA…) НЕ являются ячейками: они помечаются
// неканоничными и не проходят в QA/отгрузку. Английский КОНТЕНТ при этом
// нормален: ассет помечается каноничной локалью страны сбора, а английский
// уходит в secondary_locale (Глеб, 2026-08-25: «в Саудовской Аравии заходит
// что-то на английском — помечаем нашей каноничной ar_SA, но каждому ассету
// записываем английский в secondary»).

/**
 * Каноничная ячейка для АНГЛИЙСКОГО кода en_XX: единственная плановая локаль
 * страны + secondary 'English'. null — страна вне плана ИЛИ у неё несколько
 * локалей (en_CA неоднозначен: fr_CA/no_CA — «не подходит, потому что не в
 * списке разрешённых», решает человек).
 */
export function planCanonForEnglish(raw: string): { code: string; secondary: 'English' } | null {
  const s = normPlanCode(raw);
  const m = /^en_([A-Z]{2})$/.exec(s);
  if (!m) return null;
  const forCountry = PLAN_LOCALES.filter(l => l.iso === m[1]);
  return forCountry.length === 1 ? { code: forCountry[0].code, secondary: 'English' } : null;
}

// ─── Мост к трекеру заказчика ────────────────────────────────────────────────
// Внутренний канон норвежского — no_*; вкладки клиентского трекера — nb_*.
// Это ЕДИНСТВЕННОЕ расхождение написаний, и живёт оно только на границе
// экспорта (см. ./pothos/tracker-locales.ts — TRACKER_CODES).

/** Код вкладки трекера для планового кода ('no_GB' → 'nb_GB'). */
export function trackerCodeForPlan(raw: string): string {
  const l = planLocale(raw);
  if (!l) return '';
  return l.lang === 'no' ? 'nb_' + l.iso : l.code;
}

/** Плановый код для кода вкладки трекера ('nb_GB' → 'no_GB'). */
export function planCodeForTracker(raw: string): string {
  const s = normPlanCode(raw);
  const mapped = /^nb_/.test(s) ? 'no_' + s.slice(3) : s;
  return BY_CODE.has(mapped) ? mapped : '';
}
